import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { heroImages, womensCouture, girlsCouture, instagramPosts, brandStory } from "../data/mockData";
import { ArrowRight } from "lucide-react";

export const Home = () => {
  const [currentHeroImage, setCurrentHeroImage] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentHeroImage((prev) => (prev + 1) % heroImages.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const featuredCollections = [...womensCouture.slice(0, 3), ...girlsCouture.slice(0, 3)];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-screen overflow-hidden">
        {heroImages.map((image, index) => (
          <div
            key={index}
            className={`absolute inset-0 transition-opacity duration-2000 ${
              index === currentHeroImage ? "opacity-100" : "opacity-0"
            }`}
          >
            <img
              src={image}
              alt={`MIA Atelier Couture ${index + 1}`}
              className="w-full h-full object-cover"
            />
            {/* Dark gradient overlay for better text readability */}
            <div 
              className="absolute inset-0" 
              style={{
                background: 'linear-gradient(to bottom, rgba(0, 0, 0, 0.4) 0%, rgba(0, 0, 0, 0.3) 50%, rgba(0, 0, 0, 0.35) 100%)'
              }}
            />
          </div>
        ))}
        
        <div className="absolute inset-0 flex items-center justify-center text-center px-6">
          <div className="max-w-5xl text-white space-y-12">
            {/* Main Heading */}
            <h1 
              className="text-5xl md:text-7xl tracking-tight font-light leading-tight" 
              style={{ fontFamily: "'Playfair Display', serif", fontWeight: 300 }}
            >
              MIA Atelier
            </h1>
            
            {/* Primary Tagline */}
            <p 
              className="text-sm md:text-base tracking-[0.4em] font-light uppercase" 
              style={{ fontFamily: "'Montserrat', sans-serif", letterSpacing: '0.4em', fontWeight: 300 }}
            >
              Styled by Love. Worn with Legacy.
            </p>
            
            {/* Subtext */}
            <p 
              className="text-base md:text-lg font-light leading-loose opacity-90" 
              style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 300 }}
            >
              A luxury couture house devoted to modern heirlooms for mothers and daughters.
            </p>
            
            {/* CTA Button */}
            <div className="pt-4">
              <Link
                to="/collections"
                className="inline-flex items-center px-12 py-4 bg-[#E8DCC8]/85 text-[#2C2C2C] tracking-[0.25em] font-normal text-sm uppercase hover:bg-[#E8DCC8]/95 transition-all duration-500 ease-out border border-[#E8DCC8]/30"
                style={{ fontFamily: "'Montserrat', sans-serif" }}
              >
                Explore the Collection
                <ArrowRight className="ml-3 transition-transform duration-500 group-hover:translate-x-1" size={18} />
              </Link>
            </div>
          </div>
        </div>

        {/* Hero Navigation Dots */}
        <div className="absolute bottom-12 left-1/2 transform -translate-x-1/2 flex space-x-4">
          {heroImages.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentHeroImage(index)}
              className={`h-[1px] transition-all duration-500 ${
                index === currentHeroImage ? "bg-white w-12" : "bg-white/40 w-8"
              }`}
            />
          ))}
        </div>
      </section>

      {/* The Philosophy Section */}
      <section className="py-40 px-6 bg-[#FAF9F7]">
        <div className="max-w-3xl mx-auto text-center">
          {/* Heading */}
          <h2 
            className="text-4xl md:text-5xl mb-20 font-light tracking-wide" 
            style={{ fontFamily: "'Playfair Display', serif", fontWeight: 300 }}
          >
            The Philosophy
          </h2>

          {/* Body Content */}
          <div className="space-y-12">
            <p 
              className="text-base md:text-lg leading-loose text-[#2C2C2C] font-light"
              style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 300 }}
            >
              MIA Atelier creates modern heirlooms for mothers and daughters.
            </p>

            <p 
              className="text-base md:text-lg leading-loose text-[#2C2C2C] font-light"
              style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 300 }}
            >
              Rooted in craftsmanship and shaped by legacy, every piece is designed with intention, elegance, and quiet strength.
            </p>

            <p 
              className="text-base md:text-lg leading-loose text-[#2C2C2C] font-light"
              style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 300 }}
            >
              These are garments made to be worn today and remembered for generations.
            </p>
          </div>
        </div>
      </section>

      {/* Craftsmanship Section */}
      <section className="py-40 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          {/* Subtle divider */}
          <div className="w-24 h-[1px] bg-[#E8DCC8]/30 mx-auto mb-24" />
          
          <div className="max-w-3xl mx-auto text-center">
            {/* Heading */}
            <h2 
              className="text-4xl md:text-5xl mb-20 font-light tracking-wide" 
              style={{ fontFamily: "'Playfair Display', serif", fontWeight: 300 }}
            >
              Craftsmanship
            </h2>

            {/* Body Content */}
            <div className="space-y-12">
              <p 
                className="text-base md:text-lg leading-loose text-[#2C2C2C] font-light"
                style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 300 }}
              >
                Every MIA Atelier garment is created with care, precision, and intention. From fabric selection to the final stitch, each piece reflects refined craftsmanship and timeless elegance designed to be worn and remembered for generations.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Private Appointments Section */}
      <section className="pt-48 pb-40 px-6 bg-white">
        <div className="max-w-2xl mx-auto text-center">
          {/* Heading */}
          <h2 
            className="text-3xl md:text-4xl mb-20 font-light tracking-wide" 
            style={{ fontFamily: "'Playfair Display', serif", fontWeight: 300 }}
          >
            Private Appointments
          </h2>

          {/* Body Content */}
          <div className="space-y-12 mb-16">
            <p 
              className="text-base md:text-lg leading-loose text-[#2C2C2C] font-light"
              style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 300 }}
            >
              Experience the atelier through a personal consultation where each garment is designed with care and tailored for moments that become legacy.
            </p>
          </div>

          {/* Button */}
          <Link
            to="/contact"
            className="inline-flex items-center px-10 py-4 bg-[#E8DCC8]/80 text-[#2C2C2C] tracking-[0.25em] font-normal text-sm uppercase hover:bg-[#E8DCC8]/90 transition-all duration-500 ease-out border border-[#E8DCC8]/25"
            style={{ fontFamily: "'Montserrat', sans-serif" }}
          >
            Request a Private Appointment
          </Link>
        </div>
      </section>

      {/* About Snippet */}
      <section className="py-40 px-6 bg-[#FAF9F7]">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-24 items-center">
            <div className="order-2 md:order-1">
              <div className="relative">
                <img
                  src={brandStory.image}
                  alt="MIA Atelier Craftsmanship"
                  className="w-full h-[700px] object-cover"
                />
              </div>
            </div>
            <div className="order-1 md:order-2 md:pl-16">
              <h2 className="text-5xl md:text-6xl mb-12 font-light leading-tight" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 300 }}>
                The Atelier
              </h2>
              <p className="text-[#2C2C2C] mb-12 font-light text-base leading-loose" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                MIA Atelier was born from the desire to create beautiful, meaningful garments for mothers and daughters. Inspired by legacy and guided by craftsmanship, the atelier creates pieces that celebrate elegance, heritage, and modern femininity.
              </p>
              <Link
                to="/about"
                className="inline-flex items-center text-[#2C2C2C] hover:text-[#C4B5A0] transition-colors duration-500 tracking-[0.15em] text-sm uppercase border-b border-[#2C2C2C] hover:border-[#C4B5A0] pb-1"
                style={{ fontFamily: "'Montserrat', sans-serif" }}
              >
                Our Story
                <ArrowRight className="ml-2 transition-transform duration-500 group-hover:translate-x-1" size={16} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* The Collection */}
      <section className="py-40 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-24">
            <h2 className="text-5xl md:text-6xl mb-6 font-light" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 300 }}>
              The Collection
            </h2>
            <p className="text-[#8B7D6B] tracking-wide font-light text-sm" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              Couture for mothers and daughters
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12">
            {featuredCollections.map((item) => (
              <div
                key={item.id}
                className="group cursor-pointer overflow-hidden"
              >
                <div className="relative overflow-hidden aspect-[3/4] mb-6">
                  <img
                    src={item.image}
                    alt={item.name}
                    className="w-full h-full object-cover transition-transform duration-1000 ease-out group-hover:scale-105"
                  />
                </div>
                <div className="text-center">
                  <h3 className="text-xl mb-3 font-light" style={{ fontFamily: "'Playfair Display', serif" }}>
                    {item.name}
                  </h3>
                  <p className="text-[#8B7D6B] text-sm mb-4 font-light" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                    {item.description}
                  </p>
                  <p className="text-[#2C2C2C] tracking-wide text-sm" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                    {item.price}
                  </p>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-20">
            <Link
              to="/collections"
              className="inline-flex items-center px-12 py-4 border border-[#2C2C2C]/50 text-[#2C2C2C] tracking-[0.25em] text-sm uppercase hover:bg-[#2C2C2C] hover:text-white hover:border-[#2C2C2C] transition-all duration-500 ease-out"
              style={{ fontFamily: "'Montserrat', sans-serif" }}
            >
              View All
              <ArrowRight className="ml-3 transition-transform duration-500 group-hover:translate-x-1" size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* Instagram Feed */}
      <section className="py-40 px-6 bg-[#FAF9F7]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-20">
            <h2 className="text-4xl md:text-5xl mb-6 font-light" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 300 }}>
              Follow Our Journey
            </h2>
            <a 
              href="https://instagram.com/miaatelier.co" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-[#8B7D6B] hover:text-[#2C2C2C] tracking-wide font-light text-sm transition-colors duration-500" 
              style={{ fontFamily: "'Montserrat', sans-serif" }}
            >
              @miaatelier.co
            </a>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {instagramPosts.map((post) => (
              <div
                key={post.id}
                className="relative aspect-square overflow-hidden group cursor-pointer"
              >
                <img
                  src={post.image}
                  alt={`Instagram post ${post.id}`}
                  className="w-full h-full object-cover transition-all duration-700 ease-out group-hover:scale-110 grayscale-[20%] group-hover:grayscale-0"
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-40 px-6 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-5xl md:text-6xl mb-16 font-light leading-tight" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 300 }}>
            Create Your Legacy
          </h2>
          <Link
            to="/shop"
            className="inline-flex items-center px-12 py-4 bg-[#E8DCC8]/90 text-[#2C2C2C] tracking-[0.25em] text-sm uppercase hover:bg-[#E8DCC8] transition-all duration-500 ease-out border border-[#E8DCC8]/40"
            style={{ fontFamily: "'Montserrat', sans-serif" }}
          >
            Order Now
            <ArrowRight className="ml-3 transition-transform duration-500 group-hover:translate-x-1" size={18} />
          </Link>
        </div>
      </section>
    </div>
  );
};
